import type { DonationType } from '@/entities/donation/Donation'
import type { OrganizationCountryCode } from '@/entities/organization/Organization'
import type { CountryCode } from '@/utils/address'
import { z } from '@/utils/zod'

type CountrySpecificSchemaKey = Lowercase<OrganizationCountryCode>
type CountrySpecificSchemaShape = Record<CountrySpecificSchemaKey, z.ZodType>
type InvalidCountrySpecificSchemaKeys<Shape> = Exclude<keyof Shape, CountrySpecificSchemaKey>

export const createCountrySpecificSchema = <const Shape extends z.ZodRawShape & CountrySpecificSchemaShape>(
    shape: Shape & Record<InvalidCountrySpecificSchemaKeys<Shape>, never>
) => z.strictObject(shape)

export type OrganizationCountryProfile = {
    timezone: boolean
    holidayBlocking: boolean
    // Unit of distances entered by the organization, e.g. the mileage of compensation claims
    distanceUnit: 'km' | 'mi'
    accounting: {
        hasStockReport: boolean
    }
    donations: {
        confirmationRequired: boolean
        // Types offered when donations are recorded, stored donations keep their type
        types: DonationType[]
    }
    payments: {
        // Whether payouts to contacts are made by SEPA credit transfer, which requires an IBAN
        sepaCreditTransfer: boolean
        // Whether members can pay by SEPA direct debit; elsewhere fees are paid online or by transfer
        sepaDirectDebit: boolean
    }
    compensation: {
        // Tax-free allowances offered when compensation claims are set up, with the preset rate counting against each;
        // amounts stay editable
        limitPresets: {
            key:
                | 'trainerAllowance'
                | 'volunteerAllowance'
                | 'travelAllowance'
                | 'smallVolunteerAllowance'
                | 'largeVolunteerAllowance'
            amount: number
            dailyAmount: number | null
            monthlyAmount: number | null
            rate: 'training' | 'assignment' | null
        }[]
        // Declaration preset confirming the conditions of the allowances, null without allowances
        allowanceDeclaration: 'allowances' | 'austrianAllowances' | null
        // Whether claim documents and the yearly export list the birth date, address and social insurance number of
        // contributors, as Austrian allowance records and reports require
        identityDetails: boolean
        // Official mileage rate per kilometre offered for the travel cost rate, null where there is none
        mileageAmount: number | null
    }
    workHours: {
        // Who owes the work of new rules: families are common in the US, single memberships elsewhere
        holder: 'contact' | 'group'
        // Whether new rules offer a buyout, which US clubs commonly sell before the season
        buyout: boolean
    }
}

const defaultOrganizationCountryProfile: OrganizationCountryProfile = {
    timezone: false,
    holidayBlocking: true,
    distanceUnit: 'km',
    accounting: {
        hasStockReport: true,
    },
    donations: {
        confirmationRequired: true,
        types: ['cash', 'membershipFee', 'assetStock', 'refund', 'nonCash'],
    },
    payments: {
        sepaCreditTransfer: true,
        sepaDirectDebit: true,
    },
    compensation: {
        limitPresets: [],
        allowanceDeclaration: null,
        identityDetails: false,
        mileageAmount: null,
    },
    workHours: {
        holder: 'contact',
        buyout: false,
    },
}

export const organizationCountryProfiles = {
    DE: {
        compensation: {
            // Übungsleiterpauschale and Ehrenamtspauschale (§ 3 Nr. 26 and 26a EStG) from 2026
            limitPresets: [
                { key: 'trainerAllowance', amount: 3300_00, dailyAmount: null, monthlyAmount: null, rate: 'training' },
                { key: 'volunteerAllowance', amount: 960_00, dailyAmount: null, monthlyAmount: null, rate: null },
            ],
            allowanceDeclaration: 'allowances',
            identityDetails: false,
            // Tax-free travel cost reimbursement for trips by car
            mileageAmount: 30,
        },
    },
    AT: {
        accounting: {
            hasStockReport: false,
        },
        donations: {
            confirmationRequired: true,
            types: ['cash', 'nonCash'],
        },
        compensation: {
            // Pauschale Reiseaufwandsentschädigung (PRAE) for athletes, coaches and referees, which has no yearly cap
            // besides twelve monthly ones, and the small and large Freiwilligenpauschale for other volunteers and for
            // instructors outside physical sports, as of 2026
            limitPresets: [
                {
                    key: 'travelAllowance',
                    amount: 8640_00,
                    dailyAmount: 120_00,
                    monthlyAmount: 720_00,
                    rate: 'training',
                },
                {
                    key: 'smallVolunteerAllowance',
                    amount: 1000_00,
                    dailyAmount: 30_00,
                    monthlyAmount: null,
                    rate: 'assignment',
                },
                {
                    key: 'largeVolunteerAllowance',
                    amount: 3000_00,
                    dailyAmount: 50_00,
                    monthlyAmount: null,
                    rate: null,
                },
            ],
            allowanceDeclaration: 'austrianAllowances',
            identityDetails: true,
            // Amtliches Kilometergeld for cars from 2025
            mileageAmount: 50,
        },
    },
    US: {
        timezone: true,
        holidayBlocking: false,
        distanceUnit: 'mi',
        donations: {
            confirmationRequired: true,
            types: ['cash', 'nonCash', 'membershipFee'],
        },
        payments: {
            sepaCreditTransfer: false,
            sepaDirectDebit: false,
        },
        workHours: {
            holder: 'group',
            buyout: true,
        },
    },
} satisfies Partial<Record<CountryCode, Partial<OrganizationCountryProfile>>>

export const getOrganizationCountryProfile = (country: CountryCode): OrganizationCountryProfile => ({
    ...defaultOrganizationCountryProfile,
    ...organizationCountryProfiles[country as keyof typeof organizationCountryProfiles],
})
